import Seo from "@/components/Seo";
import { SITE, localBusinessSchema, breadcrumbSchema, serviceSchema } from "@/data/site";
import { PageHero, Section, CtaBand, Eyebrow } from "@/components/sections";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion";
import { IconBadge } from "@/components/brand";
import { WhatsAppButtonLight } from "@/components/cta";
import { Globe, ShoppingBag, LayoutDashboard, Check } from "lucide-react";

const blocks = [
  {
    icon: Globe,
    title: "Websites",
    text: "Show who you are, build trust, and capture enquiries. Great for schools, hotels, professionals and SMEs.",
    points: ["Clear pages that build trust", "Enquiry & contact forms", "Fast, mobile-first pages"],
    img: "https://images.unsplash.com/photo-1758611972971-1c8b9c6d7822?crop=entropy&cs=srgb&fm=jpg&q=70&w=1200&ixlib=rb-4.1.0",
    alt: "Ghanaian professional working on a laptop and phone at a desk",
  },
  {
    icon: ShoppingBag,
    title: "Webshops (online shops)",
    text: "Sell your products online with Mobile Money and card checkout, order notifications and a simple product manager.",
    points: ["Mobile Money & card checkout", "Order notifications", "Simple product manager"],
    img: "https://images.pexels.com/photos/7552575/pexels-photo-7552575.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "African online shop owner holding parcels ready to ship",
  },
  {
    icon: LayoutDashboard,
    title: "Web apps & portals",
    text: "Logins, dashboards and custom workflows built just for you, when a standard website isn't enough.",
    points: ["Secure logins & roles", "Dashboards & reporting", "Custom workflows"],
    img: "https://images.unsplash.com/photo-1573164574511-73c773193279?crop=entropy&cs=srgb&fm=jpg&q=70&w=1200&ixlib=rb-4.1.0",
    alt: "African team collaborating in a modern office",
  },
];

export default function Services() {
  return (
    <>
      <Seo
        title="Web Design, E-commerce & Web Apps in Ghana — Sankofa Launch Studio"
        description="We build websites, online shops with Mobile Money & card checkout, and custom web apps for Ghanaian schools, hotels and businesses. We build the right thing for your goal — not more than you need."
        jsonLd={[
          localBusinessSchema(),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]),
          serviceSchema("Website design", "Mobile-first business websites for Ghanaian organisations."),
          serviceSchema("E-commerce websites", "Online shops with Mobile Money and card checkout."),
          serviceSchema("Web application development", "Custom logins, dashboards and workflows."),
        ]}
      />
      <PageHero
        eyebrow="Services"
        breadcrumb="Home / Services"
        title="What we build"
        intro="We build the right thing for your goal — not more than you need. Websites, online shops and custom web apps for Ghanaian schools, hotels and growing businesses."
      />

      <Section className="bg-cream">
        <div className="space-y-16 md:space-y-24">
          {blocks.map((b, i) => (
            <Reveal key={b.title}>
              <article
                className={`grid grid-cols-1 items-center gap-8 md:grid-cols-2 md:gap-14 ${i % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""}`}
                data-testid={`service-block-${b.title.split(" ")[0].toLowerCase()}`}
              >
                <div>
                  <IconBadge icon={b.icon} variant="gold" />
                  <h2 className="mt-6 font-serif text-3xl font-600 text-navy md:text-4xl">{b.title}</h2>
                  <p className="mt-4 text-lg leading-relaxed text-ink">{b.text}</p>
                  <ul className="mt-6 space-y-3">
                    {b.points.map((p) => (
                      <li key={p} className="flex items-center gap-3 text-ink">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gold/20 text-gold">
                          <Check className="h-4 w-4" strokeWidth={3} />
                        </span>
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="overflow-hidden rounded-2xl shadow-softlg">
                  <img src={b.img} alt={b.alt} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <div className="rounded-2xl bg-navy p-10 text-center md:p-16" data-testid="not-sure-block">
              <Eyebrow dark>Not sure what you need?</Eyebrow>
              <h2 className="mx-auto mt-4 max-w-2xl font-serif text-3xl font-600 text-white md:text-4xl">
                Tell us your goal and budget — we'll recommend the right fit.
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-white/75">
                We never sell you more than you need.
              </p>
              <div className="mt-8 flex justify-center">
                <a
                  href={`https://wa.me/${SITE.whatsappNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="services-whatsapp-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-sans font-600 text-navy-dark transition-transform duration-300 hover:-translate-y-0.5"
                >
                  Message us on WhatsApp
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
